import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, runAiJob } from "../lib/api";
import { logError } from "../lib/logError";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, SeverityDot, AccessBadge, ReservedBadge } from "./ui-helpers";
import { RequirementHover } from "./RequirementHover";
import { Button } from "./ui/button";
import { useAiConfirm } from "./AiConfirmDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sparkle, FilePdf, FileDoc, ArrowSquareOut, MagicWand, UploadSimple, CheckCircle, WarningCircle, FileText } from "@phosphor-icons/react";
import GlossaryText from "./GlossaryText";
import EvidenceDropzone from "./EvidenceDropzone";

const SECTIONS = [
  ["how_met", "How Met"],
  ["process_performed", "Process"],
  ["who_performs", "Who"],
  ["how_often", "How Often"],
  ["evidence_summary", "Evidence"],
  ["exceptions", "Exceptions"],
];

const REVIEW_STAGES = [
  { key: "draft", label: "Draft" },
  { key: "sme_input", label: "SME Input" },
  { key: "compliance_review", label: "Compliance Review" },
  { key: "other_legal_review", label: "Other / Legal" },
  { key: "compliance_final", label: "Compliance (Final)" },
  { key: "complete", label: "Complete" },
];
const REVIEW_STAGE_CLS = {
  draft: "bg-secondary text-gray-700 border-gray-300",
  sme_input: "bg-blue-50 text-blue-700 border-blue-300",
  compliance_review: "bg-amber-50 text-amber-700 border-amber-300",
  other_legal_review: "bg-purple-50 text-purple-700 border-purple-300",
  compliance_final: "bg-amber-50 text-amber-800 border-amber-400",
  complete: "bg-green-50 text-green-700 border-green-300",
};
// app roles allowed to act FROM each stage (mirrors backend RSAW_REVIEW_ROLE_GATE)
const REVIEW_ROLE_GATE = {
  draft: ["admin", "analyst"],
  sme_input: ["admin", "analyst"],
  compliance_review: ["admin", "reviewer"],
  other_legal_review: ["admin", "reviewer"],
  compliance_final: ["admin", "reviewer"],
};
const reviewLabel = (k) => (REVIEW_STAGES.find((s) => s.key === k) || {}).label || k;

function hasNarrative(item) {
  return item.narrative && Object.values(item.narrative.sections || {}).some(Boolean);
}

export function StandardRsaw({ onEdit }) {
  const { canEdit, user, aiEnabled, isSuperadmin , aiConfidentialBlocked } = useAuth();
  const { confirmAi, aiConfirmDialog } = useAiConfirm();
  const aiOff = !aiEnabled || aiConfidentialBlocked;
  const aiOffTitle = !aiEnabled ? "AI features are turned off for this entity" : aiConfidentialBlocked ? "RSAW drafting works with your evidence and is blocked while AI is set to Public-information only" : undefined;
  const [standards, setStandards] = useState([]);
  const [sid, setSid] = useState("");
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [parts, setParts] = useState([]);
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState(null);
  const [partProg, setPartProg] = useState(null);
  const [reviewComment, setReviewComment] = useState("");

  useEffect(() => { api.get("/standards").then((r) => setStandards(r.data)); }, []);
  const load = useCallback(() => {
    if (!sid) return;
    api.get(`/rsaw/standard/${sid}`).then((r) => setData(r.data));
    api.get(`/rsaw-templates?standard_id=${sid}`).then((r) => {
      setTemplates(r.data);
      if (r.data[0]) api.get(`/rsaw-templates/${r.data[0].id}/parts`).then((pr) => setParts(pr.data.parts || []));
      else setParts([]);
    });
  }, [sid]);
  useEffect(() => { setData(null); setTemplates([]); setParts([]); load(); }, [load]);

  const uploadTemplate = async (fileObj) => {
    if (!fileObj || !sid) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("standard_id", sid);
      fd.append("file", fileObj);
      const { data } = await api.post("/rsaw-templates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.filename}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const draftAllParts = async () => {
    if (!templates[0]) return;
    const tid = templates[0].id;
    const targets = parts.filter((p) => p.matched && !p.has_response);
    if (targets.length === 0) { toast.info("All parts already have a drafted response."); return; }
    if (!(await confirmAi(`Drafting ${targets.length} requirement part response(s) sends your entity's controls and evidence metadata — confidential compliance information — to AI. Confirm this is what you intend to do.`))) return;
    setPartProg({ done: 0, total: targets.length });
    let done = 0;
    for (const p of targets) {
      try { await api.post(`/rsaw-templates/${tid}/draft-part?part_label=${encodeURIComponent(p.part_label)}&confirm_confidential=1`); } catch (e) { logError(`draft part ${p.part_label}`, e); }
      done += 1; setPartProg({ done, total: targets.length });
    }
    setPartProg(null);
    toast.success(`Drafted ${done} part response(s)`);
    load();
  };

  const draftOne = async (rid) => {
    if (!(await confirmAi())) return;
    setBusy(rid);
    try { await runAiJob(`/narratives/${rid}/generate?save=true&confirm_confidential=1`); toast.success("Narrative drafted & saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const generateAll = async () => {
    if (!data) return;
    const targets = data.requirements.filter((r) => r.applicable && !hasNarrative(r) && !r.access?.gated);
    if (targets.length === 0) { toast.info("All applicable requirements already have a narrative."); return; }
    if (!(await confirmAi())) return;
    setProgress({ done: 0, total: targets.length });
    let done = 0;
    for (const r of targets) {
      try { await runAiJob(`/narratives/${r.requirement_id}/generate?save=true&confirm_confidential=1`); } catch (e) { logError(`generate narrative ${r.req_number}`, e); }
      done += 1; setProgress({ done, total: targets.length });
    }
    setProgress(null);
    toast.success(`Generated ${done} narrative(s)`);
    load();
  };

  const exportRsaw = async (fmt) => {
    setBusy(`export-${fmt}`);
    try {
      const res = await api.get(`/rsaw/standard/${sid}/export?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `RSAW_${sid}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) { toast.error("Export failed"); }
    finally { setBusy(""); }
  };

  const populateTemplate = async () => {
    if (!templates.length) return;
    const t = templates[0];
    setBusy("populate");
    try {
      const res = await api.get(`/rsaw-templates/${t.id}/fill`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `RSAW_${sid}_populated.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Official RSAW populated & downloaded");
    } catch (e) { toast.error("Populate failed"); }
    finally { setBusy(""); }
  };

  const reviewAction = async (action) => {
    if (!templates.length) return;
    const t = templates[0];
    setBusy(`review-${action}`);
    try {
      await api.post(`/rsaw-templates/${t.id}/review-action`, { action, comment: reviewComment });
      setReviewComment("");
      toast.success("Review status updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="p-6 space-y-5 max-w-5xl">
      {aiConfirmDialog}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="overline text-muted-foreground mb-1">Standard</div>
          <Select value={sid} onValueChange={setSid}>
            <SelectTrigger data-testid="rsaw-standard-select" className="rounded-none max-w-md"><SelectValue placeholder="Select a NERC standard" /></SelectTrigger>
            <SelectContent>{standards.map((s) => <SelectItem key={s.standard_id} value={s.standard_id}>{s.standard_id}{s.title ? ` — ${s.title.slice(0, 50)}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {data && canEdit && (
          <div className="flex items-center gap-2">
            <Button data-testid="rsaw-generate-all-btn" onClick={generateAll} disabled={!!progress} className="rounded-none bg-primary hover:bg-primary/90">
              <MagicWand size={16} weight="fill" className="mr-1" />{progress ? `Generating ${progress.done}/${progress.total}…` : "Auto-generate all narratives"}
            </Button>
            <Button data-testid="rsaw-export-pdf-btn" onClick={() => exportRsaw("pdf")} disabled={!!busy} variant="outline" className="rounded-none"><FilePdf size={16} className="mr-1" />PDF</Button>
            <Button data-testid="rsaw-export-docx-btn" onClick={() => exportRsaw("docx")} disabled={!!busy} variant="outline" className="rounded-none"><FileDoc size={16} className="mr-1" />Export (generic)</Button>
          </div>
        )}
      </div>

      {!sid && <p className="text-sm text-muted-foreground">Pick a standard to assemble a turnkey RSAW: narratives auto-drafted from your controls, evidence, documents and SME assignments, with completed evidence tables.</p>}
      {sid && !data && <p className="font-mono text-sm">Loading…</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border" data-testid="rsaw-counts">
            <Tile label="Requirements" value={data.counts.total} />
            <Tile label="Applicable" value={data.counts.applicable} />
            <Tile label="With narrative" value={data.counts.with_narrative} accent={data.counts.with_narrative === data.counts.applicable ? "text-emerald-600" : "text-amber-600"} />
            <Tile label="With evidence" value={data.counts.with_evidence} accent="text-emerald-600" />
          </div>

          <div className="border-2 border-emerald-700/40 bg-emerald-50/40 p-5" data-testid="rsaw-template-card">
            <div className="flex items-start gap-2 mb-2">
              <FileText size={20} className="text-emerald-700 mt-0.5" weight="fill" />
              <div className="flex-1">
                <div className="font-display font-bold">Official NERC RSAW Template</div>
                {templates.length > 0 ? (
                  <p className="text-xs text-muted-foreground">Output preserves the official document's exact layout and tables; your responses are injected into the real response cells.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Upload the official RSAW (.docx/.xlsx) for this standard to populate it automatically from your data.</p>
                )}
              </div>
            </div>

            {templates.length > 0 ? (
              <>
                <div className="flex items-center gap-2 text-sm mb-3" data-testid="rsaw-template-status">
                  <CheckCircle size={16} className="text-emerald-600" weight="fill" />
                  <span className="font-semibold">{templates[0].filename}</span>
                  <span className="text-muted-foreground">·</span>
                  {(() => { const matched = parts.filter((p) => p.matched); const done = matched.filter((p) => p.has_response).length;
                    return <span className={done === matched.length && matched.length ? "text-emerald-700" : "text-amber-700"}>{done}/{matched.length} part responses drafted</span>; })()}
                </div>
                {canEdit && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button data-testid="rsaw-draft-parts-btn" onClick={draftAllParts} disabled={!!partProg || parts.length === 0 || aiOff} title={aiOffTitle} className="rounded-none bg-primary hover:bg-primary/90">
                      <MagicWand size={16} weight="fill" className="mr-1" />{partProg ? `Drafting ${partProg.done}/${partProg.total}…` : "Draft all part responses (AI)"}
                    </Button>
                    <Button data-testid="rsaw-populate-template-btn" onClick={populateTemplate} disabled={!!busy} className="rounded-none bg-emerald-700 hover:bg-emerald-800">
                      <FileDoc size={16} className="mr-1" />{busy === "populate" ? "Populating…" : "Populate official RSAW (.docx)"}
                    </Button>
                    {isSuperadmin && (
                    <label className="text-xs text-muted-foreground flex items-center gap-1 cursor-pointer hover:text-foreground ml-1">
                      <UploadSimple size={14} /> Replace
                      <input data-testid="rsaw-replace-input" type="file" accept=".docx,.xlsx" className="hidden"
                        onChange={(e) => { uploadTemplate(e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                    )}
                  </div>
                )}

                {(() => {
                  const t = templates[0];
                  const stage = t.review_status || "draft";
                  const canAct = (REVIEW_ROLE_GATE[stage] || []).includes(user?.role);
                  const idx = REVIEW_STAGES.findIndex((s) => s.key === stage);
                  const hist = t.review_history || [];
                  return (
                    <div className="mt-4 border-t border-emerald-700/20 pt-4" data-testid="rsaw-review-panel">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="overline text-emerald-900/70">Audit Engagement Review</span>
                        <span data-testid="rsaw-review-status"
                          className={`inline-block border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${REVIEW_STAGE_CLS[stage]}`}>
                          {reviewLabel(stage)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap text-[10px] font-semibold uppercase mb-3">
                        {REVIEW_STAGES.map((s, i) => (
                          <span key={s.key}
                            className={`px-2 py-0.5 border rounded-sm ${i === idx ? REVIEW_STAGE_CLS[s.key] : i < idx ? "bg-green-50 text-green-600 border-green-200" : "bg-secondary text-gray-400 border-gray-200"}`}>
                            {s.label}
                          </span>
                        ))}
                      </div>
                      <p className="text-[11px] text-muted-foreground mb-2">
                        Sign-off does not block populate/export — it records the SME → Compliance → Other/Legal → Compliance review of the completed RSAW before an audit engagement.
                      </p>
                      {stage !== "complete" && (
                        canAct ? (
                          <div className="space-y-2">
                            <input data-testid="rsaw-review-comment" value={reviewComment}
                              onChange={(e) => setReviewComment(e.target.value)} placeholder="Comment (optional)"
                              className="w-full border border-border px-2 py-1.5 text-sm" />
                            <div className="flex flex-wrap gap-2">
                              {stage === "draft" && (
                                <Button size="sm" data-testid="rsaw-review-start" disabled={!!busy}
                                  onClick={() => reviewAction("start")} className="rounded-none bg-primary hover:bg-primary/90">
                                  Start review (send to SME)
                                </Button>
                              )}
                              {stage !== "draft" && stage !== "compliance_final" && (
                                <Button size="sm" data-testid="rsaw-review-forward" disabled={!!busy}
                                  onClick={() => reviewAction("forward")} className="rounded-none bg-primary hover:bg-primary/90">
                                  Forward to next stage
                                </Button>
                              )}
                              {stage === "compliance_final" && (
                                <Button size="sm" data-testid="rsaw-review-complete" disabled={!!busy}
                                  onClick={() => reviewAction("complete")} className="rounded-none bg-emerald-700 hover:bg-emerald-800">
                                  Approve as audit-ready
                                </Button>
                              )}
                              {stage !== "draft" && (
                                <button data-testid="rsaw-review-return" disabled={!!busy} onClick={() => reviewAction("return")}
                                  className="border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary disabled:opacity-50">
                                  Return to previous
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-amber-700" data-testid="rsaw-review-awaiting">
                            Awaiting {(REVIEW_ROLE_GATE[stage] || []).join(" / ")} to act at the “{reviewLabel(stage)}” stage.
                          </p>
                        )
                      )}
                      {stage === "complete" && (
                        <p className="text-xs text-emerald-700 font-semibold" data-testid="rsaw-review-approved">
                          ✓ Reviewed &amp; approved as audit-ready{t.review_completed_at ? ` on ${t.review_completed_at.slice(0, 10)}` : ""}.
                        </p>
                      )}
                      {hist.length > 0 && (
                        <ul className="mt-3 space-y-1" data-testid="rsaw-review-history">
                          {[...hist].reverse().slice(0, 8).map((h, i) => (
                            <li key={`${h.at || ""}-${i}`} className="text-[11px] text-muted-foreground">
                              <span className="font-semibold text-foreground">{h.by}</span>
                              <span className="uppercase text-[9px] ml-1 px-1 border border-border">{h.by_role}</span>
                              {" "}{h.action}: {reviewLabel(h.from_status)} → {reviewLabel(h.to_status)} · {new Date(h.at).toLocaleDateString()}
                              {h.comment && <span className="italic"> — “{h.comment}”</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })()}
              </>
            ) : (canEdit && isSuperadmin) ? (
              <div className="flex items-center gap-3">
                <input id="rsaw-upload-input" data-testid="rsaw-upload-input" type="file" accept=".docx,.xlsx"
                  onChange={(e) => { uploadTemplate(e.target.files?.[0]); e.target.value = ""; }}
                  className="text-sm file:mr-3 file:border file:border-border file:bg-card file:px-3 file:py-1.5 file:text-sm file:font-semibold" />
                {busy === "upload" && <span className="text-xs text-muted-foreground">Uploading…</span>}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="rsaw-no-template">No official template has been added for this standard yet. RSAW templates are a shared library managed by Claro super-admins — please ask your Claro contact to add one.</p>
            )}
          </div>

          {data.requirements.map((it) => (
            <div key={it.requirement_id} data-testid={`rsaw-req-${it.req_number}`} className="border border-border bg-card">
              <div className="flex items-center justify-between border-b border-border px-4 py-3 bg-secondary/40 gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <RequirementHover label={`${data.standard.standard_id} ${it.req_number}`} text={it.text}>
                    <span className="font-mono font-bold text-sm bg-primary text-primary-foreground px-2 py-0.5 cursor-help">{data.standard.standard_id} {it.req_number}</span>
                  </RequirementHover>
                  {it.applicable
                    ? (hasNarrative(it) ? <StatusBadge status={it.narrative.status} /> : <span className="text-[11px] font-bold uppercase text-amber-600">No narrative</span>)
                    : <span className="text-[11px] font-bold uppercase text-muted-foreground">{it.reserved ? "Reserved" : "Not applicable"}</span>}
                  <AccessBadge access={it.access} data-testid={`rsaw-access-${it.req_number}`} />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canEdit && it.applicable && !it.access?.gated && (
                    <button data-testid={`rsaw-draft-${it.req_number}`} onClick={() => draftOne(it.requirement_id)} disabled={busy === it.requirement_id || aiOff} title={aiOffTitle}
                      className="flex items-center gap-1 border border-black px-2 py-1 text-xs font-semibold hover:bg-primary hover:text-white disabled:opacity-50">
                      <Sparkle size={13} weight="fill" />{busy === it.requirement_id ? "Drafting…" : (hasNarrative(it) ? "Re-draft" : "Auto-draft")}
                    </button>
                  )}
                  <button data-testid={`rsaw-open-${it.req_number}`} onClick={() => onEdit(it.requirement_id)} className="flex items-center gap-1 text-xs font-semibold hover:text-blue-600">Edit <ArrowSquareOut size={13} /></button>
                </div>
              </div>

              <div className="p-4 space-y-3">
                <p className="text-sm text-muted-foreground"><GlossaryText text={it.text} /></p>
                {it.measure && (
                  <div className="border-l-2 border-emerald-400 dark:border-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/40 pl-3 py-2" data-testid={`rsaw-measure-${it.req_number}`}>
                    <span className="overline text-emerald-700 dark:text-emerald-300 font-bold text-[10px]">Measure — evidence auditors look for</span>
                    <p className="text-sm mt-0.5 whitespace-pre-line"><GlossaryText text={it.measure} /></p>
                  </div>
                )}
                {(it.parts || []).filter((p) => p.measure).map((p) => (
                  <div key={p.label} className="border-l-2 border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-950/30 pl-3 py-1.5" data-testid={`rsaw-measure-${it.req_number}-${p.label}`}>
                    <span className="overline text-emerald-700 dark:text-emerald-300 font-bold text-[10px]">Measure · Part {p.label}</span>
                    <p className="text-xs mt-0.5 whitespace-pre-line"><GlossaryText text={p.measure} /></p>
                  </div>
                ))}
                <div className="text-[11px] font-mono text-muted-foreground">
                  SME: <span className="font-semibold text-foreground">{it.roles.sme_primary || "—"}</span>
                  {it.roles.sme_backup ? ` / ${it.roles.sme_backup}` : ""} · Compliance: <span className="font-semibold text-foreground">{it.roles.compliance_primary || "—"}</span>
                  {it.roles.compliance_backup ? ` / ${it.roles.compliance_backup}` : ""}
                </div>

                {!it.applicable ? (
                  <p className="text-sm text-red-700"><span className="font-semibold">Not Applicable</span> — {it.applicability_reason}</p>
                ) : (
                  <>
                    {hasNarrative(it) ? (
                      <div className="border border-border bg-secondary/20 p-3 space-y-1.5" data-testid={`rsaw-narrative-${it.req_number}`}>
                        {SECTIONS.map(([k, label]) => (it.narrative.sections?.[k] ? (
                          <p key={k} className="text-sm"><span className="font-semibold">{label}: </span>{it.narrative.sections[k]}</p>
                        ) : null))}
                      </div>
                    ) : (
                      <p className="text-sm text-amber-700">No narrative yet — click Auto-draft or Auto-generate all.</p>
                    )}

                    <div>
                      <div className="overline text-muted-foreground mb-1">Evidence Table ({it.evidence_table.length})</div>
                      {it.evidence_table.length > 0 ? (
                        <table className="w-full text-xs border border-border" data-testid={`rsaw-evidence-${it.req_number}`}>
                          <thead className="bg-secondary/50"><tr>
                            <th className="text-left px-2 py-1 border-b border-border">Evidence</th>
                            <th className="text-left px-2 py-1 border-b border-border">Type / Period</th>
                            <th className="text-left px-2 py-1 border-b border-border">Supports Control(s)</th>
                            <th className="text-left px-2 py-1 border-b border-border">Owner</th>
                          </tr></thead>
                          <tbody>
                            {it.evidence_table.map((ev, i) => (
                              <tr key={`${ev.name || "ev"}-${i}`} className="border-b border-border last:border-0">
                                <td className="px-2 py-1">{ev.name}</td>
                                <td className="px-2 py-1 text-muted-foreground">{ev.type} ({ev.period})</td>
                                <td className="px-2 py-1">{ev.controls.join(", ") || "—"}</td>
                                <td className="px-2 py-1">{ev.owner}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="flex items-start gap-2 text-sm text-red-600"><SeverityDot severity="high" /><span>No evidence attached{it.gaps[0]?.prompt ? ` — ${it.gaps[0].prompt}` : "."}</span></div>
                      )}
                      {canEdit && !it.access?.gated && (
                        <div className="mt-2">
                          <EvidenceDropzone
                            requirementId={it.requirement_id}
                            parts={it.parts || []}
                            onUploaded={load}
                            compact
                            testid={`rsaw-std-evidence-dropzone-${it.req_number}`}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className="bg-card p-4">
      <div className="overline text-muted-foreground">{label}</div>
      <div className={`font-display font-black text-3xl tracking-tighter mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}
